import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { B, FONT, TRADES, WEATHER_OPTIONS, today, SHEET_IDS } from '../../lib/constants';
import { appendToSheet, ensureToken } from '../../lib/google';

export function LogPickerModal({ projects, onClose, onSuccess }) {
  const [projectId, setProjectId] = useState(projects[0]?.id || '');
  const [entry, setEntry] = useState({ log_date: today(), weather: '', trades: [], deliveries: [], inspection: '', notes: '' });
  const [deliveryInput, setDeliveryInput] = useState('');
  const [saving, setSaving] = useState(false);

  const toggleTrade = (trade) => {
    setEntry(e => ({ ...e, trades: e.trades.includes(trade) ? e.trades.filter(t => t !== trade) : [...e.trades, trade] }));
  };

  const handleSave = async () => {
    if (!entry.notes.trim()) return;
    setSaving(true);
    const deliveries = deliveryInput ? deliveryInput.split(',').map(s => s.trim()).filter(Boolean) : [];
    const logData = { project_id: parseInt(projectId), log_date: entry.log_date, weather: entry.weather, trades: entry.trades, deliveries, inspection: entry.inspection, notes: entry.notes };
    const { error } = await supabase.from('daily_logs').upsert(logData, { onConflict: 'project_id,log_date' });
    if (!error) {
      try {
        const token = localStorage.getItem('google_access_token');
        if (token && SHEET_IDS[projectId]) {
          const row = [entry.log_date, new Date(entry.log_date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long' }), entry.weather || '', entry.trades.join(', '), '', entry.notes, deliveries.join(', '), entry.inspection || '', ''];
          await appendToSheet(SHEET_IDS[projectId], 'Daily Log!A:I', [row]);
        }
      } catch(e) {}
    }
    setSaving(false);
    if (!error) { if (onSuccess) onSuccess(); onClose(); }
  };

  const inp = { background: B.mid, border: 'none', borderRadius: 8, padding: '10px 12px', color: B.white, fontSize: 14, outline: 'none', fontFamily: FONT, width: '100%', boxSizing: 'border-box', marginBottom: 10 };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 500, padding: '60px 14px 14px' }}>
      <div style={{ background: B.navy, borderRadius: 18, padding: 20, width: '100%', maxWidth: 500, maxHeight: '92vh', overflowY: 'auto', border: `1px solid ${B.slate}` }}>
        <div style={{ color: B.goldlt, fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 16 }}>Daily Log Entry</div>

        <select value={projectId} onChange={e => setProjectId(e.target.value)} style={{ ...inp, marginBottom: 10 }}>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <input type="date" value={entry.log_date} onChange={e => setEntry(v => ({ ...v, log_date: e.target.value }))} style={inp} />
          <select value={entry.weather} onChange={e => setEntry(v => ({ ...v, weather: e.target.value }))} style={{ ...inp }}>
            <option value="">Weather…</option>
            {WEATHER_OPTIONS.map(w => <option key={w}>{w}</option>)}
          </select>
        </div>

        <div style={{ color: B.muted, fontSize: 11, marginBottom: 8 }}>Trades on site</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 12 }}>
          {TRADES.map(t => (
            <button key={t} onClick={() => toggleTrade(t)}
              style={{ background: entry.trades.includes(t) ? B.goldlt : B.slate, color: entry.trades.includes(t) ? B.black : B.muted, border: `1px solid ${B.border}`, borderRadius: 20, padding: '4px 10px', cursor: 'pointer', fontSize: 11, fontFamily: FONT }}>
              {t}
            </button>
          ))}
        </div>

        <textarea placeholder="Activity / Notes…" value={entry.notes} onChange={e => setEntry(v => ({ ...v, notes: e.target.value }))} rows={4} style={{ ...inp, resize: 'vertical' }} />
        <input placeholder="Materials delivered (comma separated)" value={deliveryInput} onChange={e => setDeliveryInput(e.target.value)} style={inp} />

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={handleSave} disabled={saving || !entry.notes.trim()}
            style={{ flex: 1, background: B.goldlt, color: B.black, border: 'none', borderRadius: 10, padding: 11, cursor: 'pointer', fontSize: 14, fontWeight: 700, fontFamily: FONT, opacity: saving || !entry.notes.trim() ? 0.6 : 1 }}>
            {saving ? 'Saving…' : 'Save Entry'}
          </button>
          <button onClick={onClose} style={{ flex: 1, background: B.slate, color: B.white, border: `1px solid ${B.border}`, borderRadius: 10, padding: 11, cursor: 'pointer', fontSize: 14, fontFamily: FONT }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

export function LogEntryModal({ projectId, onClose, onSuccess, existingEntry = null }) {
  const [entry, setEntry] = useState(existingEntry ? {
    log_date: existingEntry.log_date || today(),
    weather: existingEntry.weather || '',
    trades: existingEntry.trades || [],
    deliveries: existingEntry.deliveries || [],
    inspection: existingEntry.inspection || '',
    notes: existingEntry.notes || '',
  } : {
    log_date: today(),
    weather: '',
    trades: [],
    deliveries: [],
    inspection: '',
    notes: '',
  });
  const [deliveryInput, setDeliveryInput] = useState(existingEntry?.deliveries?.join(', ') || '');
  const [saving, setSaving] = useState(false);
  const isEditing = !!existingEntry;

  const toggleTrade = (trade) => {
    setEntry(e => ({
      ...e,
      trades: e.trades.includes(trade) ? e.trades.filter(t => t !== trade) : [...e.trades, trade],
    }));
  };

  const handleSave = async () => {
    if (!entry.notes.trim()) return;
    setSaving(true);
    const deliveries = deliveryInput ? deliveryInput.split(',').map(s => s.trim()).filter(Boolean) : [];
    const logData = {
      project_id: parseInt(projectId),
      log_date: entry.log_date,
      weather: entry.weather,
      trades: entry.trades,
      deliveries,
      inspection: entry.inspection,
      notes: entry.notes,
    };

    const { error } = await supabase.from('daily_logs').upsert(logData, { onConflict: 'project_id,log_date' });

    // Sync to Google Sheets if connected
    if (!error) {
      try {
        const token = localStorage.getItem('google_access_token');
        if (token && SHEET_IDS[projectId]) {
          const row = [
            entry.log_date,
            new Date(entry.log_date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long' }),
            entry.weather || '',
            entry.trades.join(', '),
            '',
            entry.notes,
            deliveries.join(', '),
            entry.inspection || '',
            '',
          ];
          await appendToSheet(SHEET_IDS[projectId], 'Daily Log!A:I', [row]);
        }
      } catch(e) { console.log('Sheet sync skipped:', e.message); }
    }

    setSaving(false);
    if (!error) {
      if (onSuccess) onSuccess();
      onClose();
    }
  };

  const inp = { background: B.mid, border: 'none', borderRadius: 8, padding: '10px 12px', color: B.white, fontSize: 14, outline: 'none', fontFamily: FONT, width: '100%', boxSizing: 'border-box', marginBottom: 10 };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 300, padding: '60px 14px 14px' }}>
      <div style={{ background: B.navy, borderRadius: 18, padding: 20, width: '100%', maxWidth: 500, maxHeight: '92vh', overflowY: 'auto', border: `1px solid ${B.slate}` }}>
        <div style={{ color: B.goldlt, fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 16 }}>New Log Entry</div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <input type="date" value={entry.log_date} onChange={e => setEntry(v => ({ ...v, log_date: e.target.value }))} style={inp} />
          <select value={entry.weather} onChange={e => setEntry(v => ({ ...v, weather: e.target.value }))} style={{ ...inp }}>
            <option value="">Weather…</option>
            {WEATHER_OPTIONS.map(w => <option key={w}>{w}</option>)}
          </select>
        </div>

        <div style={{ color: B.muted, fontSize: 11, marginBottom: 8 }}>Trades on site</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 12 }}>
          {TRADES.map(t => (
            <button key={t} onClick={() => toggleTrade(t)}
              style={{ background: entry.trades.includes(t) ? B.goldlt : B.slate, color: entry.trades.includes(t) ? B.black : B.muted, border: `1px solid ${B.border}`, borderRadius: 20, padding: '4px 10px', cursor: 'pointer', fontSize: 11, fontFamily: FONT }}>
              {t}
            </button>
          ))}
        </div>

        <input placeholder="Deliveries (comma separated)" value={deliveryInput} onChange={e => setDeliveryInput(e.target.value)} style={inp} />
        <input placeholder="Inspection result" value={entry.inspection} onChange={e => setEntry(v => ({ ...v, inspection: e.target.value }))} style={inp} />
        <textarea placeholder="Notes…" value={entry.notes} onChange={e => setEntry(v => ({ ...v, notes: e.target.value }))} rows={4} style={{ ...inp, resize: 'vertical' }} />

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={handleSave} disabled={saving || !entry.notes.trim()}
            style={{ flex: 1, background: B.goldlt, color: B.black, border: 'none', borderRadius: 10, padding: 11, cursor: 'pointer', fontSize: 14, fontWeight: 700, fontFamily: FONT, opacity: saving || !entry.notes.trim() ? 0.6 : 1 }}>
            {saving ? 'Saving…' : 'Save Entry'}
          </button>
          <button onClick={onClose}
            style={{ flex: 1, background: B.slate, color: B.white, border: `1px solid ${B.border}`, borderRadius: 10, padding: 11, cursor: 'pointer', fontSize: 14, fontFamily: FONT }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default LogPickerModal;
